import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React from 'react';

import deepCopy from 'lib/deepCopy';
import EditableLookupConfigDisplay from 'lookup-config/EditableLookupConfigDisplay';
import {
  BASE_FIRST_STEP_OPTIONS,
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  FIRST_STEP_OPTIONS_FOR_DESTINATION,
  LookupConfigSpec,
  LookupStepType,
} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';

import {
  AutomationInputFileConfigEditorProps,
  automationFileConfigStyles,
  SectionNumber,
} from '../AutomationFileConfigUtils';
import runLookupConfigModalForLabAutomation from '../runLookupConfigModalForLabAutomation';
import {AddComponentButton} from '../RunSchemaEditorActionButton';

function getUnusedName(usedNames: Array<string>, prefix: string, idx: number = 1): string {
  const proposedName = `${prefix}${idx}`;
  if (usedNames.findIndex((value) => value === proposedName) > -1) {
    return getUnusedName(usedNames, prefix, idx + 1);
  }
  return proposedName;
}

function getUnusedDestinationName(destinationInfos: {
  [destinationName: string]: {lookupConfig: LookupConfigSpec};
}): string {
  return getUnusedName(Object.keys(destinationInfos), 'destination');
}

function SourceSection(props: AutomationInputFileConfigEditorProps): JSX.Element {
  return (
    <>
      <div className={css(automationFileConfigStyles.innerTitleContainer)}>
        <span className={css(automationFileConfigStyles.innerTitle)}>Sources</span>
        <AddComponentButton
          label="Add new source lookup"
          onClick={() => {
            runLookupConfigModalForLabAutomation({
              baseSchema: props.baseSchema,
              firstLookupStepTypes: BASE_FIRST_STEP_OPTIONS,
              intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
              finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
              onRequestSubmit: ({lookupConfig}) => {
                props.onUpdateConfig({
                  ...props.inputFileConfig,
                  rowConfigs: [...props.inputFileConfig.rowConfigs, {source: lookupConfig, columnsMap: {}}],
                });
              },
            });
          }}
          dataTestAttrs={{
            component: 'SourceSection',
            element: 'addSource',
            index: `${props.index}`,
          }}
        />
      </div>
      <div>
        {props.inputFileConfig.rowConfigs.map((rowConfig, idx) => (
          <EditableLookupConfigDisplay
            key={idx}
            baseSchema={props.baseSchema}
            lookupConfig={rowConfig.source}
            lookupType="source"
            editTestAttrs={{
              component: 'SourceSection',
              element: 'editSource',
              index: `${props.index}${idx}`,
            }}
            removeTestAttrs={{
              component: 'SourceSection',
              element: 'removeSource',
              index: `${props.index}${idx}`,
            }}
            onEdit={() => {
              runLookupConfigModalForLabAutomation({
                baseSchema: props.baseSchema,
                firstLookupStepTypes: BASE_FIRST_STEP_OPTIONS,
                intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
                finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
                initialLookupConfig: rowConfig.source,
                onRequestSubmit: ({lookupConfig}) => {
                  const newRowConfigs = deepCopy(props.inputFileConfig.rowConfigs);
                  newRowConfigs[idx].source = lookupConfig;
                  props.onUpdateConfig({
                    ...props.inputFileConfig,
                    rowConfigs: newRowConfigs,
                  });
                },
              });
            }}
            onRemove={() => {
              const inputFileConfig = deepCopy(props.inputFileConfig);
              inputFileConfig.rowConfigs.splice(idx, 1);
              if (inputFileConfig.rowConfigs.length === 0) {
                inputFileConfig.columns = [];
                inputFileConfig.sortColumns = [];
              }

              props.onUpdateConfig(inputFileConfig);
            }}
          />
        ))}
      </div>
    </>
  );
}

function DestinationSection(props: AutomationInputFileConfigEditorProps): JSX.Element {
  const destinationInfos = props.inputFileConfig.destinationInfos
    ? {...props.inputFileConfig.destinationInfos}
    : {};

  const _getDestinationName = (
    lookupConfigLabel: string | undefined,
    allowDuplicates: boolean = false
  ): string => {
    const _getValidLabel = (label: string): string =>
      getUnusedName(Object.keys(destinationInfos), `${label} `);

    if (lookupConfigLabel == null || lookupConfigLabel === '') {
      lookupConfigLabel = getUnusedDestinationName(destinationInfos);
      notifications.warn(`No label provided. Using ${lookupConfigLabel}.`);
    } else if (!allowDuplicates && destinationInfos[lookupConfigLabel] != null) {
      const newLookupConfigLabel = _getValidLabel(lookupConfigLabel);
      notifications.warn(
        `Destination "${lookupConfigLabel}" already exists. Using "${newLookupConfigLabel}".`
      );
      lookupConfigLabel = newLookupConfigLabel;
    }
    return lookupConfigLabel;
  };

  const setDestination = (lookupConfig: LookupConfigSpec, lookupConfigLabel?: string): void => {
    lookupConfigLabel = _getDestinationName(lookupConfigLabel);
    props.onUpdateConfig({
      ...props.inputFileConfig,
      destinationInfos: {
        ...props.inputFileConfig.destinationInfos,
        [lookupConfigLabel]: {lookupConfig},
      },
    });
  };

  const updateDestination = (lookupConfig: LookupConfigSpec, oldLabel: string, newLabel?: string): void => {
    newLabel = _getDestinationName(newLabel, true);
    const inputFileConfig = produce(props.inputFileConfig, (draftInputFileConfig) => {
      for (const rowConfig of draftInputFileConfig.rowConfigs) {
        if (rowConfig.destination !== oldLabel) {
          continue;
        }
        rowConfig.destination = newLabel;
      }
      delete draftInputFileConfig.destinationInfos![oldLabel];
    });
    props.onUpdateConfig({
      ...inputFileConfig,
      destinationInfos: {
        ...inputFileConfig.destinationInfos,
        [newLabel]: {lookupConfig},
      },
    });
  };

  const removeDestination = (lookupConfigLabel: string): void => {
    const inputFileConfig = produce(props.inputFileConfig, (draftInputFileConfig) => {
      delete draftInputFileConfig.destinationInfos![lookupConfigLabel];
      for (const rowConfig of draftInputFileConfig.rowConfigs) {
        if (rowConfig.destination !== lookupConfigLabel) {
          continue;
        }
        delete rowConfig.destination;
        for (const column of Object.keys(rowConfig.columnsMap)) {
          const hasDestinationLookup =
            rowConfig.columnsMap[column].lookupSteps.findIndex(
              (lookupStep) => lookupStep.type === LookupStepType.DESTINATION
            ) >= 0;
          if (hasDestinationLookup) {
            delete rowConfig.columnsMap[column];
          }
        }
      }
      if (Object.keys(draftInputFileConfig.destinationInfos!).length === 0) {
        delete draftInputFileConfig.destinationInfos;
      }
    });
    props.onUpdateConfig(inputFileConfig);
  };

  return (
    <>
      <div className={css(automationFileConfigStyles.innerTitleContainer)}>
        <span className={css(automationFileConfigStyles.innerTitle)}>Destinations</span>
        <AddComponentButton
          label="Add new destination lookup"
          onClick={() => {
            runLookupConfigModalForLabAutomation({
              baseSchema: props.baseSchema,
              firstLookupStepTypes: FIRST_STEP_OPTIONS_FOR_DESTINATION,
              intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
              finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
              onRequestSubmit: ({lookupConfig, lookupConfigLabel}) => {
                setDestination(lookupConfig, lookupConfigLabel);
              },
              initialLookupConfigLabel: '',
            });
          }}
          dataTestAttrs={{
            component: 'DestinationSection',
            element: 'addDestination',
            index: `${props.index}`,
          }}
        />
      </div>
      <div>
        {props.inputFileConfig.destinationInfos
          ? Object.entries(props.inputFileConfig.destinationInfos).map(
              ([destinationName, destinationInfo], idx) => (
                <EditableLookupConfigDisplay
                  key={idx}
                  baseSchema={props.baseSchema}
                  lookupConfig={destinationInfo.lookupConfig}
                  lookupConfigLabel={destinationName}
                  lookupType="destination"
                  editTestAttrs={{
                    component: 'DestinationSection',
                    element: 'editDestination',
                    index: `${props.index}${idx}`,
                  }}
                  removeTestAttrs={{
                    component: 'DestinationSection',
                    element: 'removeDestination',
                    index: `${props.index}${idx}`,
                  }}
                  onEdit={() => {
                    runLookupConfigModalForLabAutomation({
                      baseSchema: props.baseSchema,
                      firstLookupStepTypes: FIRST_STEP_OPTIONS_FOR_DESTINATION,
                      intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
                      finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
                      initialLookupConfig: destinationInfo.lookupConfig,
                      onRequestSubmit: ({lookupConfig, lookupConfigLabel}) => {
                        updateDestination(lookupConfig, destinationName, lookupConfigLabel);
                      },
                      initialLookupConfigLabel: destinationName,
                    });
                  }}
                  onRemove={() => {
                    removeDestination(destinationName);
                  }}
                />
              )
            )
          : null}
      </div>
    </>
  );
}

export default function AutomationInputFileConfigEditorSourceDestSection(
  props: AutomationInputFileConfigEditorProps
): JSX.Element {
  return (
    <div className={css(automationFileConfigStyles.subsection, automationFileConfigStyles.subsectionBorder)}>
      <SectionNumber sectionNumber={2} />
      <div className={css(automationFileConfigStyles.innerTitle)}>Configure sources and destinations</div>
      <div
        className={css(automationFileConfigStyles.description, automationFileConfigStyles.sectionDescription)}
      >
        They will be used later in column and row configurations.
      </div>
      <div className={css(styles.sourceDestContents)}>
        <div className={css(styles.sourceDestItem)}>
          <SourceSection {...props} />
        </div>
        <div className={css(styles.sourceDestItem)}>
          <DestinationSection {...props} />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  sourceDestContents: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  sourceDestItem: {
    flex: 0.33,
    paddingRight: '16px',
  },
});
